import type { IVector3 } from '@seddi/math-v2';
import { type BufferGeometry, type Object3D, Mesh, MeshStandardMaterial, SRGBColorSpace } from '@seddi/three';
import type { ResourcesContext } from '../resources/resourcesContext';

export type StitchRenderable = { material: MeshStandardMaterial; mesh: Mesh };

/**
 * Create renderable from geometry.
 * @param parent - Parent object in the scene graph.
 * @param geometry - Geometry to use for the mesh.
 * @param color - Color in sRGB space.
 * @param resources - Renderer context.
 */
export const create = (
  parent: Object3D,
  geometry: BufferGeometry,
  color: IVector3,
  resources: ResourcesContext,
): StitchRenderable => {
  const material = createMaterial(color, resources);
  const mesh = new Mesh(geometry, material);
  parent.add(mesh);
  // TODO: Material reference counting.
  return { material, mesh };
};

/**
 * Dispose renderable.
 * @param self - Stitch renderable.
 */
export const dispose = (self: StitchRenderable) => {
  const mesh = self.mesh;
  mesh.parent.remove(mesh);
};

/**
 * Update stitch renderable with new color.
 * @param self - Stitch renderable.
 * @param color - Color in sRGB space.
 * @param resources - Renderer context.
 */
export const updateColor = (self: StitchRenderable, color: IVector3, resources: ResourcesContext) => {
  const material = createMaterial(color, resources);
  self.mesh.material = material;
  // TODO: Material reference counting.
};

/**
 * Convert color to HEX string.
 * @param value Color in sRGB space.
 */
const convertSrgbToHex = (value: IVector3): string => {
  return (
    '#' +
    [Math.ceil(value.x * 255), Math.ceil(value.y * 255), Math.ceil(value.z * 255)]
      .map((x) => {
        const hex = x.toString(16);
        return hex.length === 1 ? '0' + hex : hex;
      })
      .join('')
  );
};

/**
 * Create stitch material.
 * @param color - Color in sRGB space.
 */
const createMaterial = (color: IVector3, resources: ResourcesContext): MeshStandardMaterial => {
  const key = `stitch_${convertSrgbToHex(color)}`;
  // Reuse stitch materials by color.
  if (resources.materialsRegistry.has(key)) {
    return resources.materialsRegistry.get(key) as MeshStandardMaterial;
  }
  // Create stitch material by color.
  const material = new MeshStandardMaterial();
  material.color.setRGB(color.x, color.y, color.z, SRGBColorSpace);
  material.roughness = 0.9;
  resources.materialsRegistry.set(key, material);
  return material;
};
